import clsx from "clsx";
import React, { FC, useEffect, useRef } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { get } from "lodash";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRouteMatch } from "react-router-dom";

import { LANG } from "~/constans/commom";
import { useGetLocationData } from "~/hooks/locationData";
import { onScrollBottomIndicator } from "~/hooks/selectIndicatorValue";
import { defaultSelectElem } from "~/libs/getData";
import { EDITOR_MANAGING_Q_INDICATORS_PERIOD_OPTIONS } from "~/pages/Editor/EditorQIndicators/options";
import {
  useGetQRatingsListNameQuery,
  useSetQRatingMutation,
} from "~/schemas/generated/graphql";
import { Field } from "~/shared/ui/Field";

import { RATING_TYPE_Q, REQUIRED_INDICATORS_INPUT_Q } from "./options";

import s from "./QRatingForm.less";

interface IProps {
  setCloseModal: () => void;
  refetch;
  QRrating?;
  prevElemId?: string;
  isEditable?: boolean;
  formId: string;
}

/** Догрузка опций при скролле выпадающего списка antd-select до низа. */
const handlePopupScroll =
  (onBottom: () => void) => (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 4) {
      onBottom();
    }
  };

const QRatingForm: FC<IProps> = ({
  setCloseModal,
  refetch,
  QRrating = {},
  prevElemId = "",
  isEditable = false,
  formId,
}) => {
  const { t } = useTranslation();
  const isEn = localStorage.getItem("language") === LANG.en;
  const { params } = useRouteMatch();
  const isNotSelectedRequiredField = useRef(false);
  const { currentId } = useGetLocationData();

  const {
    onScrollBottomIndicators,
    indicatorsForSelect,
    searchIndicator,
    loading,
  } = onScrollBottomIndicator();
  const [setQVersionMutation] = useSetQRatingMutation({
    onCompleted() {
      refetch();
      setCloseModal();
    },
  });

  const {
    id,
    LastPeriodIndicatorIdOption,
    ValueIndicatorIdOption,
    GroupIndicatorIdOption,
    AverageIndicatorIdOption,
    DifferenceIndicatorIdOption,
    GroupAverageIndicatorIdOption,
    type,
    ParentId,
    PrevElementId,
    parentName,
    number,
    prevElementName,
  } = QRrating;

  const schema = yup.object().shape({
    number: yup
      .number()
      .nullable()
      .typeError(t("validation.numeric_field"))
      .integer(t("validation.integer"))
      .min(0, t("validation.negative_area")),
    KPIen: yup
      .string()
      .max(250, t("validation.more250"))
      .required(t("validation.required")),
    KPIru: yup
      .string()
      .max(250, t("validation.more250"))
      .required(t("validation.required")),
    sort: yup
      .number()
      .typeError(t("validation.numeric_field"))
      .integer(t("validation.integer"))
      .required(t("validation.required"))
      .min(0, t("validation.negative_area")),
    format: yup
      .number()
      .typeError(t("validation.numeric_field"))
      .integer(t("validation.integer"))
      .min(0, t("validation.negative_area")),
    round: yup
      .number()
      .typeError(t("validation.numeric_field"))
      .integer(t("validation.integer"))
      .min(0, t("validation.negative_area")),
  });

  const withoutpParent = {
    value: null,
    label: t("common.without_parent"),
  };

  const ratingTypeOptions = RATING_TYPE_Q.map((option) => ({
    value: option,
    label: t(`q.${option}`),
  }));

  const { handleSubmit, control, watch, setValue, errors } = useForm({
    defaultValues: {
      ...QRrating,
      // number: number !== "" ? Number(number) : null,
      number: !!number ? Number(number) : null,
      LastPeriodIndicatorId: LastPeriodIndicatorIdOption
        ? LastPeriodIndicatorIdOption
        : withoutpParent,
      ValueIndicatorId: ValueIndicatorIdOption
        ? ValueIndicatorIdOption
        : withoutpParent,
      GroupIndicatorId: GroupIndicatorIdOption
        ? GroupIndicatorIdOption
        : withoutpParent,
      AverageIndicatorId: AverageIndicatorIdOption
        ? AverageIndicatorIdOption
        : withoutpParent,
      DifferenceIndicatorId: DifferenceIndicatorIdOption
        ? DifferenceIndicatorIdOption
        : withoutpParent,
      GroupAverageIndicatorId: GroupAverageIndicatorIdOption
        ? GroupAverageIndicatorIdOption
        : withoutpParent,
      type:
        ratingTypeOptions.find(
          (option) => option.value === (type?.value ?? type),
        ) || ratingTypeOptions[0],
      ParentId: defaultSelectElem(ParentId, parentName, "parent"),
      PrevElementId: defaultSelectElem(PrevElementId, prevElementName, "prev"),
    },
    resolver: yupResolver(schema),
  });

  const LastPeriod = watch("LastPeriodIndicatorId") || {};
  const Value = watch("ValueIndicatorId") || {};

  const onSubmit = (data) => {
    const {
      KPIen,
      KPIru,
      number,
      type,
      ParentId,
      PrevElementId,
      LastPeriodIndicatorId,
      ValueIndicatorId,
      GroupIndicatorId,
      AverageIndicatorId,
      DifferenceIndicatorId,
      GroupAverageIndicatorId,
      sort,
      round,
      format,
    } = data;

    const notSelectedIndicators = Object.keys(data).filter(
      (key) => REQUIRED_INDICATORS_INPUT_Q.includes(key) && !data[key],
    );

    if (notSelectedIndicators.length > 0) {
      isNotSelectedRequiredField.current = true;
    } else {
      isNotSelectedRequiredField.current = false;
      setQVersionMutation({
        variables: {
          id,
          number: number ? String(number) : "",
          KPIru,
          KPIen,
          type: type.value ? type.value : type,
          ParentId: ParentId.value ? ParentId.value : null,
          PrevElementId: PrevElementId.value ? PrevElementId.value : null,
          LastPeriodIndicatorId: LastPeriodIndicatorId.value,
          ValueIndicatorId: ValueIndicatorId.value,
          GroupIndicatorId: GroupIndicatorId.value,
          AverageIndicatorId: AverageIndicatorId.value,
          DifferenceIndicatorId: DifferenceIndicatorId.value,
          GroupAverageIndicatorId: GroupAverageIndicatorId.value,
          round: +round,
          sort: +sort,
          RatingsVersionId: params["id"],
          format: +format,
        },
      });
    }
  };

  const ratingId = prevElemId || PrevElementId || currentId;
  const getListValue = useGetQRatingsListNameQuery({
    variables: {
      onlyParent: false,
      RatingsId: ratingId,
    },
  });
  const getList = [
    {
      value: null,
      label: `Новый`,
    },
    ...get(
      getListValue,
      "data.QuartilesRatingGetter.getRatingsListName",
      [],
    ).map(({ id, KPIru, KPIen }) => {
      return {
        value: id,
        label: `(${id}) ${isEn ? KPIen : KPIru}`,
      };
    }),
  ];

  const getParentList = useGetQRatingsListNameQuery({
    variables: {
      onlyParent: false,
      RatingsVersionId: params["id"],
    },
  });

  const parentList = [
    {
      value: null,
      label: t("common.without_parent"),
    },
    ...get(
      getParentList,
      "data.QuartilesRatingGetter.getRatingsListName",
      [],
    ).map(({ id, KPIru, KPIen }) => {
      return {
        value: id,
        label: `(${id}) ${isEn ? KPIen : KPIru}`,
      };
    }),
  ];

  useEffect(() => {
    if (LastPeriod.value && LastPeriod.value !== Value.value) {
      setValue("ValueIndicatorId", LastPeriod);
    }
  }, [LastPeriod.value]);

  useEffect(() => {
    if (Value.value && Value.value !== LastPeriod.value) {
      setValue("LastPeriodIndicatorId", Value);
    }
  }, [Value.value]);

  return (
    <form id={formId} className={s.q_v_form} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={10}>
        <Field
          type="input"
          name="number"
          label={t("q.number")}
          control={control}
          error={errors["number"]?.message}
          disabled={!isEditable}
        />
        <Field
          type="input"
          name="KPIru"
          label={t("q.KPIru")}
          control={control}
          error={errors["KPIru"]?.message}
          disabled={!isEditable}
        />
        <Field
          type="input"
          name="KPIen"
          label={t("q.KPIen")}
          control={control}
          error={errors["KPIen"]?.message}
          disabled={!isEditable}
        />

        {EDITOR_MANAGING_Q_INDICATORS_PERIOD_OPTIONS.map((field) => (
          <Field
            key={field}
            type="select"
            name={field}
            label={t(`q.${field}`)}
            control={control}
            labelInValue
            options={indicatorsForSelect}
            loading={loading}
            showSearch
            filterOption={false}
            onSearch={(value: string) => searchIndicator(value)}
            onPopupScroll={handlePopupScroll(onScrollBottomIndicators)}
            disabled={!isEditable}
          />
        ))}

        <Field
          type="select"
          name="type"
          label={t("q.type")}
          control={control}
          labelInValue
          options={ratingTypeOptions}
          disabled={!isEditable}
        />
        <Field
          type="input"
          name="sort"
          label={t("q.sort")}
          control={control}
          error={errors["sort"]?.message}
          disabled={!isEditable}
        />
        <Field
          type="input"
          name="round"
          label={t("q.round")}
          control={control}
          error={errors["round"]?.message}
          disabled={!isEditable}
        />
        <Field
          type="input"
          name="format"
          label={t("q.format")}
          control={control}
          error={errors["format"]?.message}
          disabled={!isEditable}
        />
        <Field
          type="select"
          name="ParentId"
          label={t("q.ParentId")}
          control={control}
          labelInValue
          options={parentList}
          placement="topLeft"
          disabled={!isEditable}
        />
        <Field
          type="select"
          name="PrevElementId"
          label={t("q.PrevElementId")}
          control={control}
          labelInValue
          options={getList}
          placement="topLeft"
          disabled={PrevElementId === null ? true : !isEditable}
        />

        <div
          className={clsx(s.footnote, {
            [s.footnote_none]: isNotSelectedRequiredField.current,
          })}
        >
          {t("editorGR.asterisk")}
        </div>
      </Flex>
    </form>
  );
};
export default QRatingForm;
